import React from 'react';
import { SentinelUser } from '@modules/auth/components/types/user';
import { CustomerAccountInfo } from '@modules/auth/components/types/customer-account';

import { isTrialUser } from '@modules/auth/components/constants/accounts';
import { isFreeAccount } from '@modules/auth/components/types/account';
import config from '@config/config';

import { styled } from '@modules/common/components/ThemeProvider/utils';
import {
  ExpiresIn,
  AccountActiveCheckmark,
  UpgradeAccount,
  ValidUntil,
  AccountExpiredCheckmark,
} from './components';
import { isExpired } from './utils';
import FreeAccount from './FreeAccount';

export type AccountStatusInfoProps = {
  shAccount: CustomerAccountInfo;
};

const NoValidAccount = () => <MessageWrapper>No valid account</MessageWrapper>;

function TrialAccount({ shAccount }: AccountStatusInfoProps) {
  return (
    <Centered>
      {config.showValidTo && <ExpiresIn validTo={shAccount.validTo} />}
      <AccountValidityCheckmark shAccount={shAccount} />
      <Gap1Rem />
      <UpgradeAccount />
    </Centered>
  );
}

function OtherAccount({ shAccount }: AccountStatusInfoProps) {
  return (
    <Centered>
      <ValidUntil validTo={shAccount.validTo} />
      <AccountValidityCheckmark shAccount={shAccount} />
    </Centered>
  );
}

export function AccountValidityCheckmark({ shAccount }: AccountStatusInfoProps) {
  if (isExpired(shAccount.validTo)) {
    return <AccountExpiredCheckmark />;
  }
  return <AccountActiveCheckmark />;
}

type Props = {
  user: SentinelUser;
  shAccount: CustomerAccountInfo | undefined;
};

export function AccountStatusInfo({ shAccount, user }: Props) {
  if (shAccount === undefined) {
    return <NoValidAccount />;
  }
  if (isTrialUser(shAccount.type.id)) {
    return <TrialAccount shAccount={shAccount} />;
  }
  if (isFreeAccount(shAccount.type.id)) {
    return <FreeAccount shAccount={shAccount} userId={user.id} />;
  }
  return <OtherAccount shAccount={shAccount} />;
}

const MessageWrapper = styled('h3')`
  margin: 0 0 1rem 0;
`;

const Centered = styled('div')`
  text-align: center;
`;

const Gap1Rem = styled('div')`
  margin-bottom: 1rem;
`;
